import React from 'react';
import { ConfirmDialogProps } from '../../../types/DesignSystem/ConfirmDialog';
import Dialog from '../Dialog/Dialog';
import Button from '../Button/Button';
import Typography from '../Typography/Typography';
import { withClassName } from '../cn';

/**
 * ConfirmDialog (§ 305) — see the type file for the four decisions it makes so a
 * caller does not have to. It is a thin composition over `Dialog`: the title band,
 * the scroll-aware chrome, the modal focus cycle and the layer rung are all Dialog's;
 * what this owns is the footer's two buttons and the "refuse both routes while
 * loading" guard, which has to live in the handlers because Dialog's Escape is a
 * third route to `onCancel` that no `disabled` attribute reaches.
 */
const ConfirmDialog = React.forwardRef<HTMLButtonElement, ConfirmDialogProps>(function ConfirmDialog({
  open: openProp,
  isOpen: isOpenProp,
  title,
  message,
  secondaryMessage,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  tone,
  loading = false,
  busyText,
  onConfirm,
  onCancel,
  size = 'sm',
  closeOnOutsideClick = false,
  className = '',
  'data-testid': dataTestId,
  ...rest
}, ref) {
  // § 331 — `open` is the canonical name; `isOpen` is the deprecated alias.
  const isOpen = openProp ?? isOpenProp ?? false;
  const cancel = () => {
    if (!loading) onCancel();
  };
  const confirm = () => {
    if (!loading) onConfirm();
  };
  const testid = (suffix: string) => (dataTestId ? `${dataTestId}-${suffix}` : undefined);

  /*
    § 347 — an ALERT dialog. It asks a yes/no question, usually about something that
    cannot be undone, so screen readers must announce it as an alert and read the
    question with it: `role="alertdialog"`, described by the message (and the
    secondary message when there is one). Focus starts on Cancel, the least
    destructive action; it used to land on the panel, because the body holds no
    control. While `loading` Cancel is disabled, so Dialog falls back to the panel.
  */
  const cancelRef = React.useRef<HTMLButtonElement>(null);
  const uid = React.useId();
  const messageId = `${uid}-message`;
  const secondaryId = `${uid}-secondary-message`;
  const hasSecondary = secondaryMessage !== undefined && secondaryMessage !== null && secondaryMessage !== '';

  return (
    <Dialog
      open={isOpen}
      onClose={cancel}
      title={title}
      tone={tone}
      size={size}
      closeOnOutsideClick={closeOnOutsideClick}
      showCloseButton={false}
      role="alertdialog"
      aria-describedby={hasSecondary ? `${messageId} ${secondaryId}` : messageId}
      initialFocusRef={cancelRef}
      data-testid={dataTestId}
      footer={
        <div className="tw-flex tw-items-center tw-justify-end tw-gap-2">
          <Button
            ref={cancelRef}
            variant="secondary"
            onClick={cancel}
            disabled={loading}
            data-testid={testid('cancel')}
          >
            {cancelText}
          </Button>
          <Button
            ref={ref}
            /* § 167 — the FILLED destructive confirm, not the outlined `danger` action. */
            variant={tone === 'danger' ? 'danger-primary' : 'primary'}
            onClick={confirm}
            loading={loading}
            data-testid={testid('confirm')}
          >
            {loading && busyText ? busyText : confirmText}
          </Button>
        </div>
      }
    >
      <div {...rest} data-tone={tone} className={withClassName(`tw-space-y-2`, className)}>
        <Typography variant="body-primary" as="p" id={messageId} className="tw-text-ink" data-testid={testid('message')}>
          {message}
        </Typography>
        {hasSecondary && (
          <Typography
            variant="body-primary"
            as="p"
            id={secondaryId}
            className="tw-text-ink-secondary"
            data-testid={testid('secondary-message')}
          >
            {secondaryMessage}
          </Typography>
        )}
      </div>
    </Dialog>
  );
});

export default ConfirmDialog;
